import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function SubAdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', permissions: { manage_series: true, manage_users: false, manage_categories: true, manage_coupons: false, edit_settings: false } });

  useEffect(() => { loadAdmins(); }, []);

  async function loadAdmins() {
    try { const { data } = await api.get('/admin/sub-admins'); setAdmins(data); } catch {} finally { setLoading(false); }
  }

  function openCreate() {
    setEditing(null);
    setForm({ name: '', email: '', password: '', permissions: { manage_series: true, manage_users: false, manage_categories: true, manage_coupons: false, edit_settings: false } });
    setDialogOpen(true);
  }

  function openEdit(a) {
    setEditing(a);
    setForm({ name: a.name, email: a.email, password: '', permissions: a.permissions || {} });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    if (!editing && !form.password) { toast.error('Password required for new admin'); return; }
    try {
      if (editing) {
        await api.put(`/admin/sub-admins/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/admin/sub-admins', form);
        toast.success('Sub-admin created');
      }
      setDialogOpen(false);
      loadAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this admin?')) return;
    try { await api.delete(`/admin/sub-admins/${id}`); toast.success('Deleted'); loadAdmins(); } catch { toast.error('Failed'); }
  }

  const permLabels = { manage_series: 'Manage Test Series', manage_users: 'Manage Users', manage_categories: 'Manage Categories', manage_coupons: 'Manage Coupons', edit_settings: 'Edit Settings' };

  return (
    <div data-testid="sub-admin-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">Managing Multiple Admins</h1>
        <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={openCreate} data-testid="create-admin-btn">
          <Plus size={16} className="mr-2" /> Add Admin
        </Button>
      </div>

      <Card className="border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : admins.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-[#52525B]">No sub-admins</TableCell></TableRow>
            ) : admins.map(a => (
              <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-sm">{a.email}</TableCell>
                <TableCell className="text-xs text-[#52525B]">
                  {Object.entries(a.permissions || {}).filter(([, v]) => v).map(([k]) => permLabels[k] || k).join(', ') || 'None'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(a.id)} data-testid={`delete-admin-${a.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit Admin' : 'Create Sub-Admin'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" /></div>
            <div><Label>Password {editing ? '(leave blank to keep)' : ''}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" /></div>
            <div className="space-y-3">
              <Label>Permissions</Label>
              {Object.entries(permLabels).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-1">
                  <span className="text-sm">{label}</span>
                  <Switch checked={form.permissions[key] || false}
                    onCheckedChange={v => setForm({ ...form, permissions: { ...form.permissions, [key]: v } })}
                    data-testid={`perm-${key}`} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={handleSave} data-testid="save-admin-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
